import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, FormControl, FormLabel, Image, Select } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import { FileInput, FormFooter, FormGroup } from '../../../../common/components';
import FormInput from '../../../../common/components/FormInput/FormInput';
import {
  ImagePreviewValidation,
  isFileTooLarge,
} from '../../../../common/components/ImagePreviewValidation/ImagePreviewValidation';
import { TextEditor } from '../../../../common/components/TextEditor/TextEditor';
import { Language } from '../../../../common/models/enums/language';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { useUploadFile } from '../../../../services/news-new';
import { CreateNewsNewRequest } from '../../models/create-news-new-request';
import { NewsNewTag } from '../../models/enums/news-new-tag.enum';
import { NewsNewFormValues } from '../../models/news-new-form-values';
import { NewsNewResponse } from '../../models/news-new-response';
import { UpdateNewsNewRequest } from '../../models/update-news-new-request';
import { useNewsNewFormValidationSchema } from './hooks/use-news-new-form-validation-schema.hook';

type NewsNewFormProps = {
  isLoading: boolean;
  onSubmit(data: CreateNewsNewRequest | UpdateNewsNewRequest): void;
  model?: NewsNewResponse;
};

export const NewsNewForm = ({ isLoading, onSubmit, model }: NewsNewFormProps) => {
  const translations = useTranslations();
  const validationSchema = useNewsNewFormValidationSchema();
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
    control,
    trigger,
    formState: { errors },
  } = useForm<NewsNewFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { language: Language.En, tag: NewsNewTag.EVENTS },
  });
  const { mutateAsync: uploadFile } = useUploadFile();

  const imageNamePreview: string | undefined = watch('temporaryImage')?.[0]?.name;
  const imageSizePreview: number | undefined = watch('temporaryImage')?.[0]?.size;

  useEffect(() => {
    reset(model);
  }, [reset, model]);

  const onFormSubmit = handleSubmit(({ temporaryImage, ...form }: NewsNewFormValues) => {
    onSubmit({
      title: form.title,
      subtitle: form.subtitle,
      content: form.content,
      tag: form.tag,
      language: form.language,
      image: temporaryImage?.[0],
    });
  });

  const handleDeleteUploadedImage = () => setValue('temporaryImage', undefined);

  return (
    <form onSubmit={onFormSubmit}>
      <FormInput
        id={'title'}
        label={translations('news_new_form_title')}
        useFormRegisterReturn={register('title')}
        setValue={setValue}
        validationError={errors.title ? errors.title?.message : undefined}
      />
      <FormInput
        id={'subtitle'}
        label={translations('news_new_form_subtitle')}
        useFormRegisterReturn={register('subtitle')}
        setValue={setValue}
        validationError={errors.subtitle ? errors.subtitle?.message : undefined}
      />
      <FormGroup label={translations('news_new_form_language')} inputId="language">
        <Controller
          control={control}
          name="language"
          render={({ field: { value, onChange } }) => (
            <Select placeholder="" value={value} onChange={onChange}>
              {Object.values(Language).map((lang) => (
                <option value={lang} key={lang}>
                  {lang}
                </option>
              ))}
            </Select>
          )}
        />
      </FormGroup>
      <FormGroup label={translations('news_new_form_tag')} inputId="tag">
        <Controller
          control={control}
          name="tag"
          render={({ field: { value, onChange } }) => (
            <Select placeholder="" value={value} onChange={onChange}>
              {Object.values(NewsNewTag).map((tag) => (
                <option value={tag} key={tag}>
                  {tag}
                </option>
              ))}
            </Select>
          )}
        />
      </FormGroup>
      {model ? (
        <FormInput
          isDisabled={true}
          id={'readingTime'}
          label={translations('news_new_form_reading_time')}
          useFormRegisterReturn={register('readingTime')}
          setValue={setValue}
        />
      ) : null}
      <TextEditor<NewsNewFormValues>
        id={'content'}
        setValue={setValue}
        label={translations('news_new_form_content')}
        watch={watch}
        trigger={trigger}
        uploadFileFunction={uploadFile}
        validationError={errors.content ? errors.content?.message : undefined}
      />
      <FormControl>
        <FormLabel htmlFor="image">{translations('news_new_form_image')}</FormLabel>
        <Image id="image" src={getValues('imageUrl') || ''} />
        <Box pt={2}>
          <FileInput accept="image/*" register={register('temporaryImage')} aria-label="temporaryImage" />
          {imageNamePreview && imageSizePreview ? (
            <ImagePreviewValidation
              imageNamePreview={imageNamePreview}
              imageSizePreview={imageSizePreview}
              handleDeleteUploadedImage={handleDeleteUploadedImage}
            />
          ) : null}
        </Box>
      </FormControl>
      <FormFooter
        isLoadingSubmitResult={isLoading}
        isSubmitDisabled={imageSizePreview ? isFileTooLarge(imageSizePreview) : false}
      />
    </form>
  );
};
